import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from 'react-router';
import { canAssignRoles, canManageLabels, canSeeInventory, type AppRole } from '@infras/shared-types';
import { useAuth } from './lib/auth';
import { ASTRYX_TRIAL } from './lib/trial';
import { useMe } from './lib/useMe';
import { usePushReconcile } from './lib/usePushReconcile';
import { LoginPage } from './pages/LoginPage';
import { ActivityPage } from './pages/ActivityPage';
import CdnCostPage from './pages/CdnCostPage';
import { SearchResultsPage } from './pages/SearchResultsPage';
import { InventoryPage } from './pages/InventoryPage';
import { OverviewPage } from './pages/OverviewPage';
import { RepoDetailPage } from './pages/RepoDetailPage';
import { ClustersPage } from './pages/ClustersPage';
import { ClusterDetailPage } from './pages/ClusterDetailPage';
import { AlertsPage } from './pages/AlertsPage';
import { SecretsPage } from './pages/SecretsPage';
import { SecretDetailPage } from './pages/SecretDetailPage';
import { SchedulerPage } from './pages/SchedulerPage';
import { SchedulerJobPage } from './pages/SchedulerJobPage';
import { CatalogPage } from './pages/CatalogPage';
import { ErrorCodesPage } from './pages/ErrorCodesPage';
import { ErrorCodeDetailPage } from './pages/ErrorCodeDetailPage';
import { AccountPage } from './pages/AccountPage';
import { UsersRolesPage } from './pages/UsersRolesPage';
import { AccessReviewersPage } from './pages/AccessReviewersPage';
import { RequestQuestionsPage } from './pages/RequestQuestionsPage';
import { DocsPage } from './pages/DocsPage';
import { IncidentPage } from './pages/IncidentPage';
import { TasksPage } from './pages/TasksPage';
import TaskNewPage from './pages/TaskNewPage';
import TaskDetailPage from './pages/TaskDetailPage';
import TaskBoardPage from './pages/TaskBoardPage';
import LabelsPage from './pages/LabelsPage';
import { LegacyChangeRedirect, NewTaskRedirect } from './components/RequestRedirects';
import { ClusterAccessPage } from './pages/ClusterAccessPage';
import { AtlasProjectPage } from './pages/AtlasProjectPage';
import { AtlasMappingsPage } from './pages/AtlasMappingsPage';
import { AgentsPage } from './pages/AgentsPage';
import { AgentCommandsPage } from './pages/AgentCommandsPage';
import { AgentCommandPage } from './pages/AgentCommandPage';
import { VpnPeersPage } from './pages/VpnPeersPage';
// Astryx trial — QA and local builds only (lib/trial.ts). The dynamic import sits behind
// the build-time constant, so a build without the flag emits no Astryx chunk; lazy, so on
// QA the Astryx JS and CSS load only on this route.
const VpnPeersAstryxPage = ASTRYX_TRIAL
  ? lazy(() => import('./pages/VpnPeersAstryxPage').then((m) => ({ default: m.VpnPeersAstryxPage })))
  : null;
import { VpnSubnetsPage } from './pages/VpnSubnetsPage';
import { VpnConnectionAuditPage } from './pages/VpnConnectionAuditPage';
import { AppShell } from './components/AppShell';
import { Drawer } from './components/Drawer';
import { LegacyRedirect } from './components/LegacyRedirect';
import { Logo } from './components/Logo';

export function App() {
  const auth = useAuth();

  if (!auth.authed) {
    return <LoginPage onCredential={auth.loginWithGoogle} />;
  }

  return (
    <BrowserRouter>
      <RoleShell email={auth.user?.email} onLogout={auth.logout} />
    </BrowserRouter>
  );
}

/**
 * `/graph` was its own page before the graph became a view of `/repos`. Kept as a
 * redirect because Global Search hits and Slack links point at it — and the
 * QUERY STRING must survive: those links carry ?service= / ?cluster=, which is
 * what the graph focuses on. Dropping it would land every search hit on an
 * unfocused graph.
 */
/** Redirect that keeps the query string (`/access?q=…` → `/tasks?q=…`). */
function KeepSearchRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function GraphRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('view', 'graph');
  return <Navigate to={`/repos?${params.toString()}`} replace />;
}

/**
 * Chrome with no navigation — the only honest shell before `me` resolves, since
 * every nav item is role-gated and we don't yet know the role. Guessing one
 * would flash inventory links at a new member.
 */
function BareShell({ children }: { children: ReactNode }) {
  return (
    <div className="app-shell">
      <header className="topbar">
        <Link to="/" className="brand-link" aria-label="OptInfra home">
          <Logo />
        </Link>
        <div className="spacer" />
      </header>
      <div className="app-body">
        <main className="app-main">
          <div className="page">{children}</div>
        </main>
      </div>
    </div>
  );
}

/**
 * Role-aware shell — waits for the server-verified `me` (no flash of inventory
 * nav for a new member), then renders the surface the role permits:
 *   inventory roles → full nav + all routes;
 *   new_member      → Get Started + docs + Settings only (RolesGuard blocks the
 *                     data anyway — this is UX, the API is the enforcement).
 */
function RoleShell({ email, onLogout }: { email?: string; onLogout: () => void }) {
  const { me, loading, error } = useMe();
  // Re-claim this browser's push subscription for the CURRENT user (shared machines).
  // Above the early returns so it is never a conditional hook.
  usePushReconcile(me?.email);

  // Before `me` resolves there is no role, so the real nav can't be built — but
  // dropping the user onto an unstyled page reads as a crash. Render the chrome
  // without nav instead, so the brand and a sign-out route stay present.
  if (loading) return <BareShell>Loading your profile…</BareShell>;
  if (error || !me) {
    return (
      <BareShell>
        <p className="error">
          We couldn&apos;t load your profile, so the app doesn&apos;t know what you have access to.
        </p>
        <p className="muted">
          Signing out and back in usually fixes it. If it keeps happening, ask in #devops-infra —
          your access itself is unaffected.
        </p>
        {error && (
          <details style={{ marginTop: 12 }}>
            <summary className="muted">Details</summary>
            <p className="error">{error.message}</p>
          </details>
        )}
        <button className="btn" onClick={onLogout} style={{ marginTop: 12 }}>
          Sign out
        </button>
      </BareShell>
    );
  }

  const inventory = canSeeInventory(me.role);
  // The connection-audit log is the SOC2 audit-of-record → root only, matching
  // the @RequireRole('root') on the backing query (this is UX; the API enforces).
  const isRoot = canAssignRoles(me.role);

  return (
    <AppShell role={me.role} email={email ?? me.email} onLogout={onLogout}>
      <Routes>
        {/* First login → Get Started (onboard the agent); returning users land on
            their home — inventory for data roles, else Get Started. */}
        <Route path="/" element={<Home me={me} />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/docs/:section" element={<DocsPage />} />
        {/* back-compat for the old top-level doc routes */}
        <Route path="/get-started" element={<Navigate to="/docs/get-started" replace />} />
        <Route path="/mcp" element={<Navigate to="/docs/mcp" replace />} />
        {inventory && (
          <>
            {/* The sidebar has linked here since phase 2a but no route ever matched,
                so Overview fell through to the catch-all and redirected to /docs —
                the page was unreachable, not just unpromoted. Routed now that it
                carries the inactive-node count; `/` still lands on /repos. */}
            <Route path="/overview" element={<OverviewPage />} />
            <Route path="/repos" element={<InventoryPage />} />
            <Route path="/repos/:id" element={<RepoDetailPage />} />
            <Route path="/clusters" element={<ClustersPage />} />
            <Route path="/clusters/:name" element={<ClusterDetailPage />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/graph" element={<GraphRedirect />} />
            {/* Full-results view for the ⌘K palette's overflow row. */}
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/secrets" element={<SecretsPage />} />
            <Route path="/secrets/:name" element={<SecretDetailPage />} />
            <Route path="/activity" element={<ActivityPage />} />
            <Route path="/costs/cdn" element={<CdnCostPage />} />
            {/* Notify owners — emails a service's owners during an incident. */}
            <Route path="/incident" element={<IncidentPage />} />
            <Route path="/scheduler" element={<SchedulerPage />} />
            <Route path="/scheduler/:name" element={<SchedulerJobPage />} />
            {/* Cluster access — the merged surface. `Atlas IPs` and `Agents` were
                two nav items over ONE mechanism: the access-list entry states are
                classified from the agent heartbeats. They are one screen now, and
                the seven pages that used to be routes are drawers over it, mounted
                unchanged so nothing they can do today is lost.

                Child paths are written ABSOLUTE rather than relative. React-router
                accepts both, but `check-crumbs` reads the route table as source
                text — a relative `project/:projectKey` would read as a top-level
                path with no crumb and fail the build for the wrong reason. */}
            <Route path="/cluster-access" element={<ClusterAccessPage />}>
              {/* `mappings`, `fleet`, `commands` and `address` are separate segments
                  under `/cluster-access/…` rather than siblings of `:projectKey`, so
                  none of them can ever be mistaken for a project key. */}
              <Route
                path="/cluster-access/project/:projectKey"
                element={
                  <Drawer title="Atlas project">
                    <AtlasProjectPage />
                  </Drawer>
                }
              />
              <Route
                path="/cluster-access/mappings"
                element={
                  <Drawer title="Cluster to Atlas project mapping">
                    <AtlasMappingsPage />
                  </Drawer>
                }
              />
              <Route
                path="/cluster-access/fleet"
                element={
                  <Drawer title="Agent fleet">
                    <AgentsPage />
                  </Drawer>
                }
              />
              <Route
                path="/cluster-access/commands"
                element={
                  <Drawer title="Agent command log">
                    <AgentCommandsPage />
                  </Drawer>
                }
              />
              <Route
                path="/cluster-access/commands/:id"
                element={
                  <Drawer title="Agent command">
                    <AgentCommandPage />
                  </Drawer>
                }
              />
              {/* The per-address drawer went with the cluster × address table on
                  2026-08-26. Its URL shipped to production on 08-24 and is copy-paste
                  friendly (`?addr=<cidr>`), so it redirects to the screen rather than
                  falling through to the `*` catch-all, which lands on /docs. The
                  search is DROPPED on purpose: `addr` means nothing to any surviving
                  surface, and carrying a param no one reads is how a dead link looks
                  alive. */}
              <Route
                path="/cluster-access/address"
                element={<Navigate replace to="/cluster-access" />}
              />
            </Route>

            {/* The six URLs this merge retired. They are in Slack threads, runbooks,
                the Activity trail and the docs, so they redirect rather than 404 —
                WITH the query string, which is the half of the link that carries
                `?cluster=`, `?status=__gone__`, `?tab=pending`, `?tab=suppressed`. */}
            <Route
              path="/atlas-whitelist"
              element={
                <LegacyRedirect
                  to="/cluster-access"
                  promote={{ key: 'project', to: '/cluster-access/project/:promoted' }}
                />
              }
            />
            {/* Declared BEFORE `:projectKey`, as it was before: react-router ranks
                static segments above dynamic ones, but the order documents it. */}
            <Route
              path="/atlas-whitelist/mappings"
              element={<LegacyRedirect to="/cluster-access/mappings" />}
            />
            <Route
              path="/atlas-whitelist/:projectKey"
              element={<LegacyRedirect to="/cluster-access/project/:projectKey" />}
            />
            <Route path="/agents" element={<LegacyRedirect to="/cluster-access/fleet" />} />
            <Route
              path="/agents/commands"
              element={<LegacyRedirect to="/cluster-access/commands" />}
            />
            <Route
              path="/agents/commands/:id"
              element={<LegacyRedirect to="/cluster-access/commands/:id" />}
            />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/error-codes" element={<ErrorCodesPage />} />
            <Route path="/error-codes/:code" element={<ErrorCodeDetailPage />} />
            <Route path="/catalog/:name" element={<CatalogPage />} />
            {/* VPN Network views — read-only; enrollment stays in staff-app. */}
            <Route path="/vpn/peers" element={<VpnPeersPage />} />
            {ASTRYX_TRIAL && VpnPeersAstryxPage && (
              <Route
                path="/vpn/peers-astryx"
                element={
                  <Suspense fallback={null}>
                    <VpnPeersAstryxPage />
                  </Suspense>
                }
              />
            )}
            <Route path="/vpn/subnets" element={<VpnSubnetsPage />} />
            {isRoot && (
              <Route path="/vpn/connection-audit" element={<VpnConnectionAuditPage />} />
            )}
          </>
        )}
        {/* Tasks — ONE ticket type (access, change or blank template). Mounted for
            every role: a new member files and follows access tasks here, and the
            API shows them nothing else. Static segments (`new`, `board`) rank above
            `:id`. The board is engineer and above (the API refuses new members too): it is
            mounted for every role so a new member who follows a link sees the page's
            "needs the engineer role" notice instead of being bounced; the nav hides it. */}
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/tasks/new" element={<TaskNewPage />} />
        <Route path="/tasks/board" element={<TaskBoardPage />} />
        <Route path="/tasks/:id" element={<TaskDetailPage />} />
        {/* Old URLs (Slack, email, runbooks) — components/RequestRedirects.tsx. Every
            redirect keeps the query string. `/requests/*` is the previous name of `/tasks/*`
            (same ids); access requests kept their numbers; change requests were renumbered,
            so `/changes/:id` is looked up through `taskByLegacyCr`. */}
        <Route path="/requests" element={<KeepSearchRedirect to="/tasks" />} />
        <Route path="/requests/new" element={<KeepSearchRedirect to="/tasks/new" />} />
        <Route path="/requests/board" element={<KeepSearchRedirect to="/tasks/board" />} />
        <Route path="/requests/:id" element={<LegacyRedirect to="/tasks/:id" />} />
        <Route path="/access" element={<KeepSearchRedirect to="/tasks" />} />
        <Route path="/access/new" element={<NewTaskRedirect template="access" />} />
        <Route path="/access/:id" element={<LegacyRedirect to="/tasks/:id" />} />
        <Route path="/changes" element={<KeepSearchRedirect to="/tasks" />} />
        <Route path="/changes/new" element={<NewTaskRedirect template="change" />} />
        <Route path="/changes/:id" element={<LegacyChangeRedirect />} />
        {/* Administration is four real pages at the SAME paths the sidebar, Slack
            links and docs already point at. The tabbed SettingsPage is gone: the
            sidebar navigates these now, so a tab strip was a second nav for the
            same four destinations. */}
        <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
        <Route path="/settings/profile" element={<AccountPage />} />
        {/* PATs used to be their own tab on your own page; they're on Account now. */}
        <Route path="/settings/access" element={<Navigate to="/settings/profile" replace />} />
        <Route path="/settings/team" element={<UsersRolesPage />} />
        <Route path="/settings/reviewers" element={<AccessReviewersPage />} />
        {/* The page's new name; the sidebar and old links keep `/settings/reviewers`. */}
        <Route path="/settings/access-assignees" element={<Navigate to="/settings/reviewers" replace />} />
        <Route path="/settings/questions" element={<RequestQuestionsPage />} />
        {/* Label admin — root and maintainers (the label mutations are @RequireRole'd). */}
        {canManageLabels(me.role) && <Route path="/settings/labels" element={<LabelsPage />} />}
        {/* any other /settings/* → your own page, not a 404 */}
        <Route path="/settings/*" element={<Navigate to="/settings/profile" replace />} />
        <Route path="*" element={<Navigate to="/docs" replace />} />
      </Routes>
    </AppShell>
  );
}

/**
 * What `/` does — a REDIRECT, not a page. First visit (per browser + email) → Get
 * Started, and a new member stays there permanently since it is their only surface.
 * Everyone else → `/repos`.
 *
 * Phase 2a made `/` render the Overview. Reverted on request: the Overview isn't
 * ready to be the thing that greets you, and the repos table is what people came
 * for. The page is not deleted — it is unlinked at `/overview`, so the freshness
 * cards, the waiting-on-you band, the change-events feed and the unowned-repos
 * count all still work and can be promoted when it's ready.
 *
 * The decision is cached per email at module scope so React StrictMode's dev
 * double-mount — which would otherwise set the flag on the first mount and flip a
 * first-timer to the returning path on the remount — can't change the outcome.
 */
const landingDecided: Record<string, boolean> = {};
function Home({ me }: { me: { email: string; role: AppRole } }) {
  const email = me.email.toLowerCase();
  if (landingDecided[email] === undefined) {
    const key = `infras.onboarded.${email}`;
    landingDecided[email] = !localStorage.getItem(key);
    localStorage.setItem(key, '1');
  }
  const firstVisit = landingDecided[email];
  if (firstVisit || !canSeeInventory(me.role)) return <Navigate to="/docs/get-started" replace />;
  return <Navigate to="/repos" replace />;
}
